import React, {
  useRef,
  useState,
  useEffect,
  KeyboardEvent,
  ClipboardEvent,
  ChangeEvent,
} from "react";
import { TextInput, Text } from "design_system";
import useLabels from "hooks/useLabels";

export const CODE_LENGTH = 6;

interface VerificationCodeCoreProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  isLoading?: boolean;
  error?: string | null;
  email?: string;
  onResend?: () => void;
  onChangeEmail?: () => void;
}

/**
 * Core verification code input component without any buttons.
 * Provides just the 6-digit code input fields and email/resend info.
 */
export function VerificationCodeCore({
  value,
  onChange,
  onSubmit,
  isLoading = false,
  error,
  email,
  onResend,
  onChangeEmail,
}: VerificationCodeCoreProps) {
  const [authLabels] = useLabels("auth");
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const valueArray = value.split("").slice(0, CODE_LENGTH);
    const newDigits = Array(CODE_LENGTH).fill("");
    valueArray.forEach((digit, index) => {
      newDigits[index] = digit;
    });
    setDigits(newDigits);
  }, [value]);

  // Auto-focus first input when error occurs and code is cleared
  useEffect(() => {
    if (error && value === "" && !isLoading) {
      // Small delay to ensure DOM updates are complete
      const timer = setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [error, value, isLoading]);

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CODE_LENGTH);

    if (pasteData.length > 0) {
      const newDigits = [...digits];
      const pasteArray = pasteData.split("");

      for (let i = 0; i < pasteArray.length && index + i < CODE_LENGTH; i++) {
        newDigits[index + i] = pasteArray[i];
      }

      const newValue = newDigits.join("");
      setDigits(newDigits);
      onChange(newValue);

      // Focus next input if not complete
      const nextEmptyIndex = newDigits.findIndex((d) => d === "");
      const focusIndex =
        nextEmptyIndex === -1 ? CODE_LENGTH - 1 : nextEmptyIndex;
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleChange = (index: number, newValue: string) => {
    const digit = newValue.replace(/\D/g, "").slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    const newCode = newDigits.join("");
    setDigits(newDigits);
    onChange(newCode);

    // Auto-advance focus to next input
    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (!digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "Enter") {
      if (digits.every((d) => d !== "") && onSubmit) {
        onSubmit();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  return (
    <div className="flex flex-col gap-6 items-center">
      <div>
        <div className="flex gap-2 justify-center">
          {digits.map((digit, index) => (
            <TextInput
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              id={`verification-digit-${index}`}
              value={digit}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleChange(index, e.target.value)
              }
              onPaste={(e: ClipboardEvent<HTMLInputElement>) =>
                handlePaste(e, index)
              }
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
                handleKeyDown(e, index)
              }
              placeholder=""
              maxLength={1}
              className="!w-[42px] !px-0"
              inputClassName="text-center lg-mono-bold flex-none w-full"
              isDisabled={isLoading}
              inputMode="numeric"
              pattern="[0-9]"
              autoComplete="one-time-code"
              aria-label={`${authLabels.labels.digit} ${index + 1}`}
              autoFocus={index === 0}
            />
          ))}
        </div>
        {error && (
          <div role="alert" className="mt-2">
            <Text size="sm" className="text-alert-fg-base! text-center">
              {error}
            </Text>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center justify-center">
        {email && (
          <Text size="x-sm" theme="muted" align="center">
            {authLabels.messages["verification-code-sent"]} {email}.
          </Text>
        )}

        <Text size="x-sm" theme="muted" align="center">
          {authLabels.messages["did-not-get-it"]}{" "}
          {onResend && (
            <button
              onClick={onResend}
              disabled={isLoading}
              className="text-fg-muted underline underline-offset-2 disabled:opacity-50"
            >
              {authLabels.buttons.resend}
            </button>
          )}
          {onResend && onChangeEmail && ` ${authLabels.messages.or} `}
          {onChangeEmail && (
            <button
              onClick={onChangeEmail}
              disabled={isLoading}
              className="text-fg-muted underline underline-offset-2 disabled:opacity-50"
            >
              {authLabels.links["use-another-email"]}
            </button>
          )}
          .
        </Text>
      </div>
    </div>
  );
}
